import {
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    DashboardClaim,
    RelationshipInformation
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetAccountSnapshotByRelationshipRequest,
    GetAccountSnapshotByRelationshipService,
    GetAgingOverviewByRelationshipRequest,
    GetAgingOverviewByRelationshipService,
    GetMetricsByRelationshipRequest,
    GetMetricsByRelationshipService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { getRouterLinkById, ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { max } from 'date-fns';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { filter, first } from 'rxjs/operators';
import {
    atlas,
    customerAtlas,
    INCREASE_CREDIT_LIMIT
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { CustomerDetailService } from '../customer-detail.service';

@LifecycleAware()
@Component({
    selector: 'overview-by-customer',
    templateUrl: 'overview-by-customer.component.html',
    styleUrls: ['./overview-by-customer.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OverviewByCustomerComponent implements OnInit, OnDestroy {
    @ViewChild('bannerContentTemplate')
    public bannerContentTemplate: TemplateRef<any>;

    public accountSnapshotByRelationshipDataSource = new LsqDataSource({
        data$: this._getAccountSnapshotByRelationshipService.dataStream,
        loading$: this._getAccountSnapshotByRelationshipService.isInFlightStream,
        error$: this._getAccountSnapshotByRelationshipService.errorStream
    });
    public agingOverviewByRelationshipDataSource = new LsqDataSource({
        data$: this._getAgingOverviewByRelationshipService.dataStream,
        loading$: this._getAgingOverviewByRelationshipService.isInFlightStream,
        error$: this._getAgingOverviewByRelationshipService.errorStream
    });
    public metricsDataSource = new LsqDataSource({
        data$: this._getMetricsService.dataStream,
        loading$: this._getMetricsService.isInFlightStream,
        error$: this._getMetricsService.errorStream
    });

    public isMobile = false;
    public relationshipInformation: RelationshipInformation;
    private _debtorName: string;
    private _lastUpdatedDate: Date;
    public debtorKey: number;

    public claim = DashboardClaim;
    public increaseCreditLimitRouterLink = getRouterLinkById(atlas, INCREASE_CREDIT_LIMIT);

    public creditLimitCopy = this._i18n({
        value: 'Credit Limit',
        meaning: 'The credit limit of a debtor'
    });

    constructor(
        private _getAccountSnapshotByRelationshipService: GetAccountSnapshotByRelationshipService,
        private _getAgingOverviewByRelationshipService: GetAgingOverviewByRelationshipService,
        private _getMetricsService: GetMetricsByRelationshipService,
        private _responsiveService: ResponsiveService,
        private _titleService: Title,
        private _customerDetailService: CustomerDetailService,
        private _i18n: I18n
    ) {}

    public ngOnDestroy(): void {}

    // Getters
    public get debtorName(): string {
        return this._debtorName;
    }

    public get lastUpdatedDate(): Date {
        return this._lastUpdatedDate;
    }

    // Setters
    public set debtorName(val: string) {
        this._debtorName = val;
        // Overwrite the browser tab title to include the company's name.
        this._titleService.setTitle(`${val} - LSQ`);
    }

    public set lastUpdatedDate(val: Date) {
        if (val) {
            if (!this._lastUpdatedDate) {
                this._lastUpdatedDate = val;
            } else {
                this._lastUpdatedDate = max(val, this._lastUpdatedDate);
            }
        }
    }

    // Lifecycle Methods
    public ngOnInit(): void {
        this._customerDetailService.bannerDataStream
            .pipe(
                filter(({ debtorKey }) => !!debtorKey),
                first()
            )
            .subscribe(({ debtorKey }) => {
                this.debtorKey = debtorKey;

                // Call the api.
                this._getAccountSnapshotByRelationshipService.execute(
                    new GetAccountSnapshotByRelationshipRequest({ debtorKey: this.debtorKey })
                );
                this._getAgingOverviewByRelationshipService.execute(
                    new GetAgingOverviewByRelationshipRequest({ debtorKey: this.debtorKey })
                );
                this._getMetricsService.execute(
                    new GetMetricsByRelationshipRequest({ debtorKey: this.debtorKey })
                );

                this._getAccountSnapshotByRelationshipService.dataStream
                    .pipe(forLifeOf(this))
                    .subscribe((response) => {
                        if (response.lastUpdatedDate) {
                            this._lastUpdatedDate = response.lastUpdatedDate;
                            this.updateCustomerDetailBanner();
                        }
                    });

                this._getAgingOverviewByRelationshipService.dataStream
                    .pipe(forLifeOf(this))
                    .subscribe((response) => {
                        if (response.lastUpdatedDate) {
                            this._lastUpdatedDate = response.lastUpdatedDate;
                            this.updateCustomerDetailBanner();
                        }
                    });

                this._getMetricsService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
                    if (response.lastUpdatedDate) {
                        this._lastUpdatedDate = response.lastUpdatedDate;
                        this.updateCustomerDetailBanner();
                    }
                });

                this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
                    this.isMobile = isMobile;
                });
            });

        this._customerDetailService.relationshipInformationStream
            .pipe(forLifeOf(this))
            .subscribe((relationshipInformation) => {
                // Set relationship information.
                this.relationshipInformation = relationshipInformation;
                this.debtorName = this.relationshipInformation.debtorName;
            });
    }

    public updateCustomerDetailBanner(): void {
        this._customerDetailService.hideExport = true;
        this._customerDetailService.updateBanner({
            title: this.debtorName,
            debtorKey: this.debtorKey,
            debtorName: this.debtorName,
            lastUpdatedDate: this.lastUpdatedDate || undefined,
            datePickerConfiguration: undefined,
            reportId: customerAtlas.CUSTOMER_OVERVIEW.path
        });
    }
}
